import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  ChevronRight,
  Clock3,
  FileCheck2,
  Home,
  ListChecks,
  Loader2,
  Plus,
  Search,
  UserRound,
  UsersRound,
} from "lucide-react";
import { apiFetch, type CurrentUser } from "./lib/api";
import "./customer-crm-workbench.css";

type CrmView = "today" | "intake" | "followup" | "weekly";
export type DeadlineStatus =
  | "complete"
  | "not_started"
  | "open"
  | "due_soon"
  | "overdue";

type CustomerAccessProfile = {
  canAccess: boolean;
  canCreate: boolean;
  allScopes: boolean;
  allowedScopes: Array<{
    businessCategory: CustomerIntakeDraft["businessCategory"];
    commerceChannel: CustomerIntakeDraft["commerceChannel"] | null;
  }>;
};

export type CustomerRow = {
  id: string;
  createdAt?: string;
  name: string;
  customerName?: string;
  companyName?: string | null;
  contactName?: string | null;
  contactTitle?: string | null;
  mobile?: string | null;
  wechat?: string | null;
  customerType?: string | null;
  province?: string | null;
  city?: string | null;
  status: string;
  nextAction?: string | null;
  nextContactAt?: string | null;
  latestFollowupSummary?: string | null;
  intakeMissing?: string[];
  intakeComplete?: boolean;
  intakeCompleteness?: number;
  intakeDeadlineStatus?: DeadlineStatus;
  intakeHoursRemaining?: number | null;
  owner?: { id: string; displayName: string };
};

export type GovernanceSummary = {
  scope: "team" | "mine";
  stats: {
    customerCount: number;
    activeCount: number;
    missingEvidenceCount: number;
    intakeCompleteCount?: number;
    intakeOverdueCount?: number;
    intakeDueSoonCount?: number;
    intakeCompletenessRate?: number;
  };
  owners: Array<{
    ownerUserId: string;
    displayName: string;
    customerCount: number;
    attentionCount: number;
    intakeCompleteCount?: number;
  }>;
  rows: CustomerRow[];
};

type CustomerIntakeDraft = {
  customerName: string;
  companyName: string;
  contactName: string;
  contactTitle: string;
  mobile: string;
  wechatId: string;
  businessCategory: "ECOMMERCE" | "AGRICULTURE" | "INDUSTRY" | "OTHER_INDUSTRY";
  commerceChannel: "ONLINE" | "OFFLINE" | "";
  customerType: string;
  province: string;
  city: string;
  cooperationContent: string;
  expectedScale: string;
  procurementProcess: string;
  sampleStatus: string;
  firstSubstantiveContactAt: string;
  followupType: string;
  followupContent: string;
  nextAction: string;
  nextContactAt: string;
};

type FollowupDraft = {
  customerId: string;
  followupType: string;
  content: string;
  nextAction: string;
  nextContactAt: string;
};

const customerTypeOptions: Record<
  CustomerIntakeDraft["businessCategory"],
  string[]
> = {
  ECOMMERCE: ["代理商", "超商", "CS店", "零售门店", "其他C端渠道"],
  AGRICULTURE: ["农业项目", "合作社", "种植基地"],
  INDUSTRY: ["工业客户", "生产企业", "园区项目"],
  OTHER_INDUSTRY: ["养殖业", "服务业", "其他行业"],
};

function localDateTime(date = new Date()) {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function buildInitialIntake(): CustomerIntakeDraft {
  const next = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000);
  next.setHours(10, 0, 0, 0);
  return {
    customerName: "",
    companyName: "",
    contactName: "",
    contactTitle: "",
    mobile: "",
    wechatId: "",
    businessCategory: "ECOMMERCE",
    commerceChannel: "OFFLINE",
    customerType: "代理商",
    province: "",
    city: "",
    cooperationContent: "",
    expectedScale: "",
    procurementProcess: "",
    sampleStatus: "未寄送",
    firstSubstantiveContactAt: localDateTime(),
    followupType: "线下拜访",
    followupContent: "",
    nextAction: "",
    nextContactAt: localDateTime(next),
  };
}

const previewCustomers: CustomerRow[] = [
  {
    id: "preview-customer-1",
    name: "北京丰禾商贸",
    customerName: "北京丰禾商贸",
    contactName: "周明远",
    contactTitle: "渠道采购经理",
    customerType: "代理商",
    province: "北京",
    city: "朝阳区",
    status: "CONTACTED",
    nextAction: "补沟通截图和下一步日期",
    intakeMissing: ["沟通截图", "下一步日期"],
    intakeComplete: false,
    intakeCompleteness: 83,
    intakeDeadlineStatus: "due_soon",
    intakeHoursRemaining: 3,
    nextContactAt: localDateTime(new Date(new Date().setHours(16, 30, 0, 0))),
    owner: { id: "preview-sales", displayName: "孙小松" },
  },
  {
    id: "preview-customer-2",
    name: "青禾社区商业",
    customerName: "青禾社区商业",
    contactName: "张岚",
    contactTitle: "采购负责人",
    customerType: "超商",
    province: "北京",
    city: "海淀区",
    status: "MET",
    nextAction: "确认首批试销规模",
    intakeMissing: [],
    intakeComplete: true,
    intakeCompleteness: 100,
    intakeDeadlineStatus: "complete",
    nextContactAt: localDateTime(new Date(new Date().setHours(17, 30, 0, 0))),
    owner: { id: "preview-sales", displayName: "孙小松" },
  },
  {
    id: "preview-customer-3",
    name: "北方优选供应链",
    customerName: "北方优选供应链",
    contactName: "王女士",
    contactTitle: "渠道总监",
    customerType: "区域代理",
    province: "河北",
    city: "石家庄",
    status: "CONTACTED",
    nextAction: "发送报价并安排复盘",
    intakeMissing: ["预计规模"],
    intakeComplete: false,
    intakeCompleteness: 92,
    intakeDeadlineStatus: "open",
    intakeHoursRemaining: 18,
    nextContactAt: localDateTime(new Date(Date.now() + 24 * 60 * 60 * 1000)),
    owner: { id: "preview-sales", displayName: "孙小松" },
  },
];

export const previewSummary: GovernanceSummary = {
  scope: "team",
  stats: {
    customerCount: 27,
    activeCount: 19,
    missingEvidenceCount: 3,
    intakeCompleteCount: 23,
    intakeOverdueCount: 1,
    intakeDueSoonCount: 1,
    intakeCompletenessRate: 85,
  },
  owners: [
    {
      ownerUserId: "preview-sales",
      displayName: "孙小松",
      customerCount: 12,
      attentionCount: 3,
      intakeCompleteCount: 9,
    },
    {
      ownerUserId: "preview-wang",
      displayName: "王潇",
      customerCount: 8,
      attentionCount: 0,
      intakeCompleteCount: 8,
    },
    {
      ownerUserId: "preview-li",
      displayName: "李昊",
      customerCount: 7,
      attentionCount: 1,
      intakeCompleteCount: 6,
    },
  ],
  rows: previewCustomers,
};

function customerName(customer: CustomerRow) {
  return customer.name || customer.customerName || "未命名客户";
}

function deadlineLabel(customer: CustomerRow) {
  if (customer.intakeDeadlineStatus === "overdue") return "已超过24小时";
  if (customer.intakeDeadlineStatus === "due_soon")
    return `还剩 ${customer.intakeHoursRemaining ?? 0} 小时`;
  if (customer.intakeDeadlineStatus === "complete") return "建档完整";
  if (customer.intakeDeadlineStatus === "not_started") return "待记录沟通时间";
  return customer.intakeHoursRemaining === null ||
    customer.intakeHoursRemaining === undefined
    ? "待处理"
    : `还剩 ${customer.intakeHoursRemaining} 小时`;
}

export function CustomerCrmWorkbench({
  user,
  initialView = "today",
  initialCustomerId = "",
  initialOwnerUserId = "",
  initialWeekStart = "",
}: {
  user: CurrentUser;
  initialView?: CrmView;
  initialCustomerId?: string;
  initialOwnerUserId?: string;
  initialWeekStart?: string;
}) {
  const previewMode = import.meta.env.DEV && user.id.startsWith("preview-");
  const draftKey = `huigui_customer_intake_draft_${user.id}`;
  const [view, setView] = useState<CrmView>(initialView);
  const [access, setAccess] = useState<CustomerAccessProfile | null>(null);
  const [summary, setSummary] = useState<GovernanceSummary | null>(null);
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [intake, setIntake] = useState<CustomerIntakeDraft>(buildInitialIntake);
  const [intakeEvidence, setIntakeEvidence] = useState<File | null>(null);
  const [savingIntake, setSavingIntake] = useState(false);
  const [intakeSuccess, setIntakeSuccess] = useState("");
  const [pendingUpload, setPendingUpload] = useState<{
    customerId: string;
    followupId: string;
  } | null>(null);
  const [followup, setFollowup] = useState<FollowupDraft>({
    customerId: initialCustomerId,
    followupType: "线下拜访",
    content: "",
    nextAction: "",
    nextContactAt: localDateTime(new Date(Date.now() + 24 * 60 * 60 * 1000)),
  });
  const [followupEvidence, setFollowupEvidence] = useState<File | null>(null);
  const [savingFollowup, setSavingFollowup] = useState(false);
  const [followupSuccess, setFollowupSuccess] = useState("");

  async function loadData() {
    setLoading(true);
    setError("");
    try {
      if (previewMode) {
        setAccess({
          canAccess: true,
          canCreate: true,
          allScopes: true,
          allowedScopes: [],
        });
        setSummary(previewSummary);
        setCustomers(previewCustomers);
        setFollowup((current) => ({
          ...current,
          customerId:
            current.customerId || initialCustomerId || previewCustomers[0].id,
        }));
        return;
      }
      const [profile, governance, list] = await Promise.all([
        apiFetch<CustomerAccessProfile>("/customers/access-profile"),
        apiFetch<GovernanceSummary>("/sales-governance/summary"),
        apiFetch<{ items: CustomerRow[] }>("/customers?page=1&pageSize=100"),
      ]);
      setAccess(profile);
      setSummary(governance);
      const governanceById = new Map(
        governance.rows.map((item) => [item.id, item]),
      );
      const enrichedCustomers = list.items.map((item) => ({
        ...item,
        ...governanceById.get(item.id),
        name:
          item.name ||
          governanceById.get(item.id)?.customerName ||
          "未命名客户",
      }));
      setCustomers(enrichedCustomers);
      setFollowup((current) => ({
        ...current,
        customerId:
          current.customerId ||
          initialCustomerId ||
          enrichedCustomers[0]?.id ||
          "",
      }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "读取客户工作台失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(draftKey);
      if (stored)
        setIntake({
          ...buildInitialIntake(),
          ...(JSON.parse(stored) as Partial<CustomerIntakeDraft>),
        });
    } catch {
      window.localStorage.removeItem(draftKey);
    }
    void loadData();
  }, [draftKey]);

  useEffect(() => {
    if (!intake.customerName && !intake.contactName && !intake.followupContent)
      return;
    window.localStorage.setItem(draftKey, JSON.stringify(intake));
  }, [draftKey, intake]);

  const visibleCustomers = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return customers;
    return customers.filter((customer) =>
      [
        customerName(customer),
        customer.companyName,
        customer.contactName,
        customer.mobile,
        customer.wechat,
      ]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(keyword)),
    );
  }, [customers, search]);

  const attentionCustomers = useMemo(
    () =>
      (summary?.rows ?? customers)
        .filter(
          (customer) => !customer.intakeComplete || customer.nextContactAt,
        )
        .sort((left, right) => {
          const weight: Record<DeadlineStatus, number> = {
            overdue: 0,
            due_soon: 1,
            open: 2,
            not_started: 3,
            complete: 4,
          };
          return (
            weight[left.intakeDeadlineStatus ?? "not_started"] -
            weight[right.intakeDeadlineStatus ?? "not_started"]
          );
        })
        .slice(0, 8),
    [customers, summary],
  );

  const weeklyCustomers = useMemo(() => {
    const source = summary?.rows ?? customers;
    const weekStart = initialWeekStart
      ? new Date(`${initialWeekStart}T00:00:00+08:00`)
      : null;
    const weekEnd = weekStart
      ? new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000)
      : null;

    return source.filter((customer) => {
      if (
        initialOwnerUserId &&
        customer.owner?.id !== initialOwnerUserId
      ) {
        return false;
      }
      if (!weekStart || !weekEnd) return true;
      const createdAt = customer.createdAt ? new Date(customer.createdAt) : null;
      return Boolean(
        createdAt &&
          !Number.isNaN(createdAt.getTime()) &&
          createdAt >= weekStart &&
          createdAt < weekEnd,
      );
    });
  }, [customers, initialOwnerUserId, initialWeekStart, summary]);

  const weeklyOwner = useMemo(
    () =>
      initialOwnerUserId
        ? summary?.owners.find(
            (owner) => owner.ownerUserId === initialOwnerUserId,
          )
        : null,
    [initialOwnerUserId, summary],
  );

  const weeklyStats = useMemo(() => {
    if (!initialOwnerUserId && !initialWeekStart) return summary?.stats;
    const completeCount = weeklyCustomers.filter(
      (customer) => customer.intakeComplete,
    ).length;
    const overdueCount = weeklyCustomers.filter(
      (customer) => customer.intakeDeadlineStatus === "overdue",
    ).length;
    const dueSoonCount = weeklyCustomers.filter(
      (customer) => customer.intakeDeadlineStatus === "due_soon",
    ).length;
    return {
      customerCount: weeklyCustomers.length,
      activeCount: weeklyCustomers.length,
      missingEvidenceCount: weeklyCustomers.filter((customer) =>
        customer.intakeMissing?.includes("沟通截图"),
      ).length,
      intakeCompleteCount: completeCount,
      intakeOverdueCount: overdueCount,
      intakeDueSoonCount: dueSoonCount,
      intakeCompletenessRate: weeklyCustomers.length
        ? Math.round((completeCount / weeklyCustomers.length) * 100)
        : 100,
    };
  }, [initialOwnerUserId, initialWeekStart, summary, weeklyCustomers]);

  function patchIntake(patch: Partial<CustomerIntakeDraft>) {
    setIntake((current) => ({ ...current, ...patch }));
    setIntakeSuccess("");
    setError("");
  }

  async function uploadEvidence(
    customerId: string,
    followupId: string,
    file: File,
  ) {
    if (previewMode) return;
    const body = new FormData();
    body.append("file", file);
    body.append("category", "COMMUNICATION_EVIDENCE");
    body.append("followupId", followupId);
    body.append("note", "企业微信手机端现场沟通证据");
    await apiFetch(`/customers/${encodeURIComponent(customerId)}/evidence`, {
      method: "POST",
      body,
    });
  }

  async function submitIntake(event: FormEvent) {
    event.preventDefault();
    if (!intakeEvidence) {
      setError("请先上传本次实质沟通截图。客户归属与提成资料需要这项证据。");
      return;
    }
    if (!intake.mobile.trim() && !intake.wechatId.trim()) {
      setError("手机号和微信号至少填写一项。");
      return;
    }
    setSavingIntake(true);
    setError("");
    try {
      if (pendingUpload) {
        await uploadEvidence(
          pendingUpload.customerId,
          pendingUpload.followupId,
          intakeEvidence,
        );
      } else if (previewMode) {
        await new Promise((resolve) => window.setTimeout(resolve, 450));
      } else {
        const duplicate = await apiFetch<{
          canCreate: boolean;
          message: string;
        }>("/customers/duplicate-check", {
          method: "POST",
          body: JSON.stringify({
            customerName: intake.customerName,
            companyName: intake.companyName || undefined,
            contactName: intake.contactName,
            mobile: intake.mobile || undefined,
            wechatId: intake.wechatId || undefined,
          }),
        });
        if (!duplicate.canCreate) throw new Error(duplicate.message);
        const created = await apiFetch<CustomerRow>("/customers", {
          method: "POST",
          body: JSON.stringify({
            customerName: intake.customerName,
            companyName: intake.companyName || undefined,
            contactName: intake.contactName,
            contactTitle: intake.contactTitle,
            mobile: intake.mobile || undefined,
            wechatId: intake.wechatId || undefined,
            province: intake.province,
            city: intake.city,
            source: "企业微信手机CRM",
            businessCategory: intake.businessCategory,
            commerceChannel:
              intake.businessCategory === "ECOMMERCE"
                ? intake.commerceChannel
                : undefined,
            customerType: intake.customerType,
            ownerUserId: user.id,
            status: "CONTACTED",
            cooperationContent: intake.cooperationContent,
            expectedScale: intake.expectedScale,
            procurementProcess: intake.procurementProcess || undefined,
            sampleStatus: intake.sampleStatus || undefined,
            firstSubstantiveContactAt: new Date(
              intake.firstSubstantiveContactAt,
            ).toISOString(),
          }),
        });
        const createdFollowup = await apiFetch<{ id: string }>(
          `/customers/${encodeURIComponent(created.id)}/followups`,
          {
            method: "POST",
            body: JSON.stringify({
              followupDate: new Date(
                intake.firstSubstantiveContactAt,
              ).toISOString(),
              followupType: intake.followupType,
              content: intake.followupContent,
              keyPoints: `客户需求：${intake.cooperationContent}\n预计规模：${intake.expectedScale}`,
              nextAction: intake.nextAction,
              nextContactAt: new Date(intake.nextContactAt).toISOString(),
              needReminder: true,
            }),
          },
        );
        setPendingUpload({
          customerId: created.id,
          followupId: createdFollowup.id,
        });
        await uploadEvidence(created.id, createdFollowup.id, intakeEvidence);
      }
      window.localStorage.removeItem(draftKey);
      setPendingUpload(null);
      setIntakeSuccess("客户资料、首条跟进、截图和下次提醒已完成保存。");
      setIntake(buildInitialIntake());
      setIntakeEvidence(null);
      await loadData();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "客户建档失败");
    } finally {
      setSavingIntake(false);
    }
  }

  async function submitFollowup(event: FormEvent) {
    event.preventDefault();
    if (
      !followup.customerId ||
      !followup.content.trim() ||
      !followup.nextAction.trim() ||
      !followup.nextContactAt
    ) {
      setError("请选择客户，并补齐沟通结论、下一步动作和时间。");
      return;
    }
    if (!followupEvidence) {
      setError("请为本次实质沟通上传截图或现场照片。");
      return;
    }
    setSavingFollowup(true);
    setError("");
    try {
      if (previewMode) {
        await new Promise((resolve) => window.setTimeout(resolve, 450));
      } else {
        const created = await apiFetch<{ id: string }>(
          `/customers/${encodeURIComponent(followup.customerId)}/followups`,
          {
            method: "POST",
            body: JSON.stringify({
              followupDate: new Date().toISOString(),
              followupType: followup.followupType,
              content: followup.content,
              nextAction: followup.nextAction,
              nextContactAt: new Date(followup.nextContactAt).toISOString(),
              needReminder: true,
            }),
          },
        );
        await uploadEvidence(followup.customerId, created.id, followupEvidence);
      }
      setFollowupSuccess("跟进、证据和企业微信提醒已一起更新。");
      setFollowup((current) => ({ ...current, content: "", nextAction: "" }));
      setFollowupEvidence(null);
      await loadData();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "保存跟进失败");
    } finally {
      setSavingFollowup(false);
    }
  }

  if (loading && !summary) {
    return (
      <main className="customer-crm-loading">
        <Loader2 className="spin" size={24} />
        正在读取客户工作台...
      </main>
    );
  }

  if (access && !access.canAccess) {
    return (
      <main className="customer-crm-loading">
        <AlertTriangle size={26} />
        <strong>当前账号没有一般CRM权限</strong>
        <p>这里不会显示道冲客户。请由管理员配置线下渠道客户范围。</p>
      </main>
    );
  }

  return (
    <main className="customer-crm-page">
      <section className="customer-crm-shell">
        <header className="customer-crm-header">
          <div>
            <span>大爱归心 · 线下渠道CRM</span>
            <h1>
              {view === "today"
                ? "今日客户"
                : view === "intake"
                  ? "现场建档"
                  : view === "followup"
                    ? "快速跟进"
                    : "完整性检查"}
            </h1>
          </div>
          <span className="customer-crm-avatar">
            {(user.displayName || user.name || "孙").slice(0, 1)}
          </span>
        </header>

        {error ? (
          <div className="customer-crm-alert danger">
            <AlertTriangle size={17} />
            <span>{error}</span>
            <button onClick={() => setError("")} type="button">
              关闭
            </button>
          </div>
        ) : null}
        {intakeSuccess || followupSuccess ? (
          <div className="customer-crm-alert success">
            <CheckCircle2 size={17} />
            <span>{intakeSuccess || followupSuccess}</span>
          </div>
        ) : null}

        <div className="customer-crm-content">
          {view === "today" ? renderToday() : null}
          {view === "intake" ? renderIntake() : null}
          {view === "followup" ? renderFollowup() : null}
          {view === "weekly" ? renderWeekly() : null}
        </div>

        <nav className="customer-crm-nav" aria-label="客户工作台导航">
          <button
            className={view === "today" ? "active" : ""}
            onClick={() => setView("today")}
            type="button"
          >
            <Home size={21} />
            <span>今日</span>
          </button>
          <button
            className={view === "intake" ? "active" : ""}
            disabled={access ? !access.canCreate : false}
            onClick={() => setView("intake")}
            type="button"
          >
            <Plus size={23} />
            <span>建档</span>
          </button>
          <button
            className={view === "followup" ? "active" : ""}
            onClick={() => setView("followup")}
            type="button"
          >
            <FileCheck2 size={21} />
            <span>跟进</span>
          </button>
          <button
            className={view === "weekly" ? "active" : ""}
            onClick={() => setView("weekly")}
            type="button"
          >
            <ListChecks size={21} />
            <span>检查</span>
          </button>
        </nav>
      </section>
    </main>
  );

  function renderToday() {
    const stats = summary?.stats;
    return (
      <div className="customer-crm-stack">
        <section className="customer-crm-greeting">
          <div>
            <span>
              {new Date().toLocaleDateString("zh-CN", {
                month: "long",
                day: "numeric",
                weekday: "short",
              })}
            </span>
            <h2>{user.displayName || user.name}，先处理最接近时限的客户。</h2>
            <p>只提醒缺口，不重复填写已经完成的内容。</p>
          </div>
          <button onClick={() => setView("intake")} type="button">
            <Plus size={18} />
            新增客户
          </button>
        </section>
        {(stats?.intakeDueSoonCount ?? 0) + (stats?.intakeOverdueCount ?? 0) >
        0 ? (
          <section className="customer-crm-deadline">
            <Clock3 size={21} />
            <div>
              <strong>
                {(stats?.intakeDueSoonCount ?? 0) +
                  (stats?.intakeOverdueCount ?? 0)}
                位客户需要尽快补齐
              </strong>
              <span>含接近24小时与已经逾期的记录</span>
            </div>
            <button onClick={() => setView("weekly")} type="button">
              查看
            </button>
          </section>
        ) : null}
        <section className="customer-crm-metrics">
          <article>
            <span>我的客户</span>
            <strong>{stats?.customerCount ?? customers.length}</strong>
            <small>一般CRM</small>
          </article>
          <article>
            <span>24小时完整</span>
            <strong>{stats?.intakeCompletenessRate ?? 100}%</strong>
            <small>{stats?.intakeCompleteCount ?? 0}条已完整</small>
          </article>
          <article>
            <span>待补证据</span>
            <strong>{stats?.missingEvidenceCount ?? 0}</strong>
            <small>可直接补交</small>
          </article>
        </section>
        <section className="customer-crm-panel">
          <div className="customer-crm-title">
            <div>
              <span>今日动作</span>
              <h3>待处理客户</h3>
            </div>
            <button onClick={() => setView("weekly")} type="button">
              全部
            </button>
          </div>
          <div className="customer-crm-customer-list">
            {attentionCustomers.length ? (
              attentionCustomers.map((customer) => (
                <button
                  className="customer-crm-customer"
                  key={customer.id}
                  onClick={() => {
                    setFollowup((current) => ({
                      ...current,
                      customerId: customer.id,
                    }));
                    setView("followup");
                  }}
                  type="button"
                >
                  <span
                    className={`customer-crm-dot ${customer.intakeDeadlineStatus ?? "not_started"}`}
                  />
                  <span>
                    <strong>{customerName(customer)}</strong>
                    <small>
                      {customer.contactName || "联系人待补"} ·{" "}
                      {customer.customerType || "类型待补"}
                    </small>
                  </span>
                  <span>
                    <strong>
                      {customer.nextAction ||
                        customer.intakeMissing?.[0] ||
                        "继续跟进"}
                    </strong>
                    <small>{deadlineLabel(customer)}</small>
                  </span>
                  <ChevronRight size={17} />
                </button>
              ))
            ) : (
              <div className="customer-crm-empty">
                <CheckCircle2 size={27} />
                <strong>今天没有待补客户</strong>
                <span>可以新增客户或检查下次跟进。</span>
              </div>
            )}
          </div>
        </section>
        <section className="customer-crm-panel compact">
          <div className="customer-crm-title">
            <div>
              <span>客户清单</span>
              <h3>搜索我的客户</h3>
            </div>
          </div>
          <label className="customer-crm-search">
            <Search size={17} />
            <input
              aria-label="搜索客户"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="客户、联系人或手机号"
              value={search}
            />
          </label>
          <div className="customer-crm-mini-list">
            {visibleCustomers.slice(0, 8).map((customer) => (
              <button
                key={customer.id}
                onClick={() => {
                  setFollowup((current) => ({
                    ...current,
                    customerId: customer.id,
                  }));
                  setView("followup");
                }}
                type="button"
              >
                <span>
                  <strong>{customerName(customer)}</strong>
                  <small>{customer.contactName || "联系人待补"}</small>
                </span>
                <em>{customer.intakeCompleteness ?? 0}%</em>
              </button>
            ))}
          </div>
        </section>
      </div>
    );
  }

  function renderIntake() {
    const types = customerTypeOptions[intake.businessCategory];
    return (
      <form className="customer-crm-form" onSubmit={submitIntake}>
        <section className="customer-crm-form-intro">
          <div>
            <span>一次完成 · 自动保存草稿</span>
            <h2>现场客户建档</h2>
            <p>客户资料、实质沟通、截图和下一步一次提交。</p>
          </div>
          <em>24h</em>
        </section>
        <section className="customer-crm-form-section">
          <header>
            <b>01</b>
            <div>
              <h3>客户与联系人</h3>
              <p>用于全库查重和客户归属</p>
            </div>
          </header>
          <div className="customer-crm-fields">
            <label>
              <span>客户名称 *</span>
              <input
                required
                value={intake.customerName}
                onChange={(event) =>
                  patchIntake({ customerName: event.target.value })
                }
              />
            </label>
            <label>
              <span>企业全称</span>
              <input
                value={intake.companyName}
                onChange={(event) =>
                  patchIntake({ companyName: event.target.value })
                }
              />
            </label>
            <label>
              <span>联系人 *</span>
              <input
                required
                value={intake.contactName}
                onChange={(event) =>
                  patchIntake({ contactName: event.target.value })
                }
              />
            </label>
            <label>
              <span>联系人职务 *</span>
              <input
                required
                value={intake.contactTitle}
                onChange={(event) =>
                  patchIntake({ contactTitle: event.target.value })
                }
              />
            </label>
            <label>
              <span>手机号</span>
              <input
                inputMode="tel"
                value={intake.mobile}
                onChange={(event) =>
                  patchIntake({ mobile: event.target.value })
                }
              />
            </label>
            <label>
              <span>微信号</span>
              <input
                value={intake.wechatId}
                onChange={(event) =>
                  patchIntake({ wechatId: event.target.value })
                }
              />
            </label>
            <label>
              <span>客户分类 *</span>
              <select
                value={intake.businessCategory}
                onChange={(event) => {
                  const businessCategory = event.target
                    .value as CustomerIntakeDraft["businessCategory"];
                  patchIntake({
                    businessCategory,
                    commerceChannel:
                      businessCategory === "ECOMMERCE" ? "OFFLINE" : "",
                    customerType: customerTypeOptions[businessCategory][0],
                  });
                }}
              >
                {Object.keys(customerTypeOptions).map((value) => (
                  <option key={value} value={value}>
                    {value === "ECOMMERCE"
                      ? "电商渠道"
                      : value === "AGRICULTURE"
                        ? "农业"
                        : value === "INDUSTRY"
                          ? "工业"
                          : "其他行业"}
                  </option>
                ))}
              </select>
            </label>
            {intake.businessCategory === "ECOMMERCE" ? (
              <label>
                <span>线上 / 线下 *</span>
                <select
                  value={intake.commerceChannel}
                  onChange={(event) =>
                    patchIntake({
                      commerceChannel: event.target.value as
                        | "ONLINE"
                        | "OFFLINE",
                    })
                  }
                >
                  <option value="OFFLINE">线下</option>
                  <option value="ONLINE">线上</option>
                </select>
              </label>
            ) : null}
            <label>
              <span>渠道类型 *</span>
              <select
                value={intake.customerType}
                onChange={(event) =>
                  patchIntake({ customerType: event.target.value })
                }
              >
                {types.map((type) => (
                  <option key={type}>{type}</option>
                ))}
              </select>
            </label>
            <label>
              <span>省份 *</span>
              <input
                required
                value={intake.province}
                onChange={(event) =>
                  patchIntake({ province: event.target.value })
                }
              />
            </label>
            <label>
              <span>城市 / 区域 *</span>
              <input
                required
                value={intake.city}
                onChange={(event) => patchIntake({ city: event.target.value })}
              />
            </label>
          </div>
        </section>
        <section className="customer-crm-form-section">
          <header>
            <b>02</b>
            <div>
              <h3>本次实质沟通</h3>
              <p>写结论，不要求长篇纪要</p>
            </div>
          </header>
          <div className="customer-crm-fields">
            <label className="wide">
              <span>客户需求 *</span>
              <textarea
                required
                rows={3}
                value={intake.cooperationContent}
                onChange={(event) =>
                  patchIntake({ cooperationContent: event.target.value })
                }
              />
            </label>
            <label className="wide">
              <span>预计规模 *</span>
              <textarea
                required
                rows={2}
                value={intake.expectedScale}
                onChange={(event) =>
                  patchIntake({ expectedScale: event.target.value })
                }
                placeholder="例如：12家门店，首批120箱"
              />
            </label>
            <label>
              <span>沟通方式 *</span>
              <select
                value={intake.followupType}
                onChange={(event) =>
                  patchIntake({ followupType: event.target.value })
                }
              >
                <option>线下拜访</option>
                <option>企业微信</option>
                <option>电话</option>
                <option>视频会议</option>
              </select>
            </label>
            <label>
              <span>实质沟通时间 *</span>
              <input
                required
                type="datetime-local"
                value={intake.firstSubstantiveContactAt}
                onChange={(event) =>
                  patchIntake({ firstSubstantiveContactAt: event.target.value })
                }
              />
            </label>
            <label className="wide">
              <span>沟通结论 *</span>
              <textarea
                required
                rows={3}
                value={intake.followupContent}
                onChange={(event) =>
                  patchIntake({ followupContent: event.target.value })
                }
              />
            </label>
            <label>
              <span>下一步动作 *</span>
              <input
                required
                value={intake.nextAction}
                onChange={(event) =>
                  patchIntake({ nextAction: event.target.value })
                }
              />
            </label>
            <label>
              <span>完成日期 *</span>
              <input
                required
                type="datetime-local"
                value={intake.nextContactAt}
                onChange={(event) =>
                  patchIntake({ nextContactAt: event.target.value })
                }
              />
            </label>
          </div>
        </section>
        <details className="customer-crm-optional">
          <summary>补充采购流程和样品状态</summary>
          <div className="customer-crm-fields">
            <label className="wide">
              <span>采购流程</span>
              <textarea
                rows={2}
                value={intake.procurementProcess}
                onChange={(event) =>
                  patchIntake({ procurementProcess: event.target.value })
                }
              />
            </label>
            <label>
              <span>样品状态</span>
              <select
                value={intake.sampleStatus}
                onChange={(event) =>
                  patchIntake({ sampleStatus: event.target.value })
                }
              >
                <option>未寄送</option>
                <option>已寄送</option>
                <option>已签收</option>
                <option>已反馈</option>
              </select>
            </label>
          </div>
        </details>
        <section className="customer-crm-form-section">
          <header>
            <b>03</b>
            <div>
              <h3>沟通证据</h3>
              <p>请先遮盖无关个人信息</p>
            </div>
          </header>
          <label
            className={`customer-crm-upload ${intakeEvidence ? "done" : ""}`}
          >
            <Camera size={25} />
            <strong>
              {intakeEvidence ? intakeEvidence.name : "拍照或上传聊天截图"}
            </strong>
            <span>
              {intakeEvidence
                ? "点击可重新选择"
                : "支持微信截图、拜访照片或会议纪要"}
            </span>
            <input
              accept="image/*,.pdf"
              capture="environment"
              onChange={(event) =>
                setIntakeEvidence(event.target.files?.[0] ?? null)
              }
              type="file"
            />
          </label>
        </section>
        <footer className="customer-crm-submit">
          <div>
            <strong>
              {intakeEvidence ? "资料齐全，可以提交" : "还缺沟通截图"}
            </strong>
            <span>
              {pendingUpload
                ? "客户和跟进已保存，本次只重试截图"
                : "提交前会再次全库查重"}
            </span>
          </div>
          <button disabled={savingIntake} type="submit">
            {savingIntake ? (
              <Loader2 className="spin" size={18} />
            ) : (
              <CheckCircle2 size={18} />
            )}
            {pendingUpload ? "重试截图" : "完成建档"}
          </button>
        </footer>
      </form>
    );
  }

  function renderFollowup() {
    const selected = customers.find(
      (customer) => customer.id === followup.customerId,
    );
    return (
      <form className="customer-crm-form" onSubmit={submitFollowup}>
        <section className="customer-crm-form-intro">
          <div>
            <span>两分钟完成</span>
            <h2>快速跟进</h2>
            <p>跟进、截图和下次提醒一起保存。</p>
          </div>
          <FileCheck2 size={30} />
        </section>
        <section className="customer-crm-panel compact">
          <label className="customer-crm-search">
            <Search size={17} />
            <select
              aria-label="选择客户"
              value={followup.customerId}
              onChange={(event) =>
                setFollowup((current) => ({
                  ...current,
                  customerId: event.target.value,
                }))
              }
            >
              <option value="">请选择客户</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customerName(customer)} ·{" "}
                  {customer.contactName || "联系人待补"}
                </option>
              ))}
            </select>
          </label>
          {selected ? (
            <div className="customer-crm-selected">
              <span>{customerName(selected).slice(0, 1)}</span>
              <div>
                <strong>{customerName(selected)}</strong>
                <small>
                  {selected.contactName || "联系人待补"} ·{" "}
                  {selected.contactTitle || "职务待补"}
                </small>
              </div>
              <em>{selected.intakeCompleteness ?? 0}%</em>
            </div>
          ) : null}
        </section>
        <section className="customer-crm-form-section">
          <header>
            <b>01</b>
            <div>
              <h3>本次沟通</h3>
              <p>关键结论和下一步为必填</p>
            </div>
          </header>
          <div className="customer-crm-fields">
            <label>
              <span>沟通方式 *</span>
              <select
                value={followup.followupType}
                onChange={(event) =>
                  setFollowup((current) => ({
                    ...current,
                    followupType: event.target.value,
                  }))
                }
              >
                <option>线下拜访</option>
                <option>企业微信</option>
                <option>电话</option>
                <option>样品反馈</option>
                <option>报价沟通</option>
              </select>
            </label>
            <label className="wide">
              <span>沟通结论 *</span>
              <textarea
                rows={4}
                value={followup.content}
                onChange={(event) =>
                  setFollowup((current) => ({
                    ...current,
                    content: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              <span>下一步动作 *</span>
              <input
                value={followup.nextAction}
                onChange={(event) =>
                  setFollowup((current) => ({
                    ...current,
                    nextAction: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              <span>下次时间 *</span>
              <input
                type="datetime-local"
                value={followup.nextContactAt}
                onChange={(event) =>
                  setFollowup((current) => ({
                    ...current,
                    nextContactAt: event.target.value,
                  }))
                }
              />
            </label>
          </div>
        </section>
        <section className="customer-crm-form-section">
          <header>
            <b>02</b>
            <div>
              <h3>本次证据</h3>
              <p>和这条跟进记录绑定</p>
            </div>
          </header>
          <label
            className={`customer-crm-upload ${followupEvidence ? "done" : ""}`}
          >
            <Camera size={25} />
            <strong>
              {followupEvidence ? followupEvidence.name : "添加截图或现场照片"}
            </strong>
            <span>提交后自动更新客户完整度</span>
            <input
              accept="image/*,.pdf"
              capture="environment"
              onChange={(event) =>
                setFollowupEvidence(event.target.files?.[0] ?? null)
              }
              type="file"
            />
          </label>
        </section>
        <footer className="customer-crm-submit">
          <div>
            <strong>提交后自动生成提醒</strong>
            <span>企业微信将按下次时间提醒负责人</span>
          </div>
          <button disabled={savingFollowup} type="submit">
            {savingFollowup ? (
              <Loader2 className="spin" size={18} />
            ) : (
              <FileCheck2 size={18} />
            )}
            保存跟进与证据
          </button>
        </footer>
      </form>
    );
  }

  function renderWeekly() {
    const stats = weeklyStats;
    const isFocusedReview = Boolean(initialOwnerUserId || initialWeekStart);
    return (
      <div className="customer-crm-stack">
        <section className="customer-crm-greeting">
          <div>
            <span>{initialWeekStart ? `${initialWeekStart} 当周核对` : "本周CRM完整性"}</span>
            <h2>
              {weeklyOwner
                ? `${weeklyOwner.displayName}的客户录入`
                : "缺什么、谁来补、何时完成。"}
            </h2>
            <p>
              {isFocusedReview
                ? "点击每位客户，可直接核对资料和沟通截图。"
                : summary?.scope === "team"
                ? "当前显示团队范围。"
                : "当前只显示本人负责的客户。"}
            </p>
          </div>
          <button onClick={() => void loadData()} type="button">
            刷新
          </button>
        </section>
        <section className="customer-crm-metrics">
          <article>
            <span>客户总数</span>
            <strong>{stats?.customerCount ?? 0}</strong>
            <small>本权限范围</small>
          </article>
          <article>
            <span>24小时完整</span>
            <strong>{stats?.intakeCompletenessRate ?? 100}%</strong>
            <small>{stats?.intakeCompleteCount ?? 0}条完整</small>
          </article>
          <article>
            <span>逾期 / 临期</span>
            <strong>
              {(stats?.intakeOverdueCount ?? 0) +
                (stats?.intakeDueSoonCount ?? 0)}
            </strong>
            <small>优先补齐</small>
          </article>
        </section>
        {summary?.scope === "team" && summary.owners.length && !isFocusedReview ? (
          <section className="customer-crm-panel">
            <div className="customer-crm-title">
              <div>
                <span>团队检查</span>
                <h3>成员完整度</h3>
              </div>
              <UsersRound size={20} />
            </div>
            <div className="customer-crm-owner-list">
              {summary.owners.map((owner) => {
                const percent = owner.customerCount
                  ? Math.round(
                      ((owner.intakeCompleteCount ??
                        owner.customerCount - owner.attentionCount) /
                        owner.customerCount) *
                        100,
                    )
                  : 100;
                return (
                  <article key={owner.ownerUserId}>
                    <span>{owner.displayName.slice(0, 1)}</span>
                    <div>
                      <strong>{owner.displayName}</strong>
                      <small>
                        {owner.intakeCompleteCount ??
                          owner.customerCount - owner.attentionCount}
                        /{owner.customerCount}条完整
                      </small>
                      <i>
                        <b style={{ width: `${percent}%` }} />
                      </i>
                    </div>
                    <em>{percent}%</em>
                  </article>
                );
              })}
            </div>
          </section>
        ) : null}
        <section className="customer-crm-panel">
          <div className="customer-crm-title">
            <div>
              <span>{isFocusedReview ? "当周录入" : "合同口径"}</span>
              <h3>{isFocusedReview ? "客户核对清单" : "客户缺失项"}</h3>
            </div>
            <ListChecks size={20} />
          </div>
          <div className="customer-crm-audit-list">
            {weeklyCustomers
              .filter((customer) => isFocusedReview || !customer.intakeComplete)
              .map((customer) => (
                <button
                  key={customer.id}
                  onClick={() => {
                    if (isFocusedReview) {
                      window.location.href = `/mobile/work/customers/review?customerId=${encodeURIComponent(customer.id)}`;
                      return;
                    }
                    setFollowup((current) => ({
                      ...current,
                      customerId: customer.id,
                    }));
                    setView("followup");
                  }}
                  type="button"
                >
                  <span>
                    <strong>{customerName(customer)}</strong>
                    <small>
                      {customer.intakeMissing?.join(" · ") || "待补资料"}
                    </small>
                  </span>
                  <span>
                    <em>{customer.intakeCompleteness ?? 0}%</em>
                    <small>{deadlineLabel(customer)}</small>
                  </span>
                  <ChevronRight size={17} />
                </button>
              ))}
            {!weeklyCustomers.length ? (
              <div className="customer-crm-empty">
                <CheckCircle2 size={27} />
                <strong>当周没有客户录入</strong>
                <span>本周新增客户后会在这里汇总。</span>
              </div>
            ) : null}
          </div>
        </section>
        <section className="customer-crm-note">
          <UserRound size={19} />
          <div>
            <strong>签约前条件按阶段出现</strong>
            <p>
              采购流程、样品、创办人视频、商业条款审批、合同和回款不会挤在首次建档页；进入相应阶段后再提示补齐。
            </p>
          </div>
        </section>
      </div>
    );
  }
}
